import { readonly, ref, useContext } from "@nuxtjs/composition-api";
import { Logger } from "~/helpers/logger";
import { getProductListCommand } from "~/modules/catalog/product/composables/useProduct/commands/getProductListCommand";
import { getProductDetailsCommand } from "~/modules/catalog/product/composables/useProduct/commands/getProductDetailsCommand";
import type { GetProductSearchParams } from "~/modules/catalog/product/types";
import type { ProductInterface, ProductParent } from "~/modules/GraphQL/types";
import type { ProductDetails, ProductList, UseProductErrors, UseProductInterface } from "./useProduct";
import getProductListBySkusGql from "~/modules/checkout/composables/useCart/queries/getProductListBySkus.gql";
import getProductListBySkusCateIdGql from "~/modules/checkout/composables/useCart/queries/getProductListBySkusCateId.gql";
import { GET_PRODUCT_SEARCH } from "~/modules/catalog/product/queries/getProduct.gql";

import customSearchProductGql from "~/modules/catalog/product/queries/customSearchProductGql.gql";
interface ProductsResponse {
  productSearch: {
    items: ProductParent[];
    [key: string]: any;
  };
}
interface SearchProductsResponse {
  productSearch: {
    items: any[];
    [key: string]: any;
  };
}
/**
 * Allows loading product details or list with
 * params for sorting, filtering and pagination.
 *
 * See the {@link UseProductInterface} for a list of methods and values available in this composable.
 */
export function useProduct(id?: string): UseProductInterface {
  const loading = ref(false);
  const error = ref<UseProductErrors>({
    getProductList: null,
    getProductDetails: null,
    getProductListBySkus: null,
    getProductListBySkusAndCateId: null,
    searchProducts: null,
  });

  const context = useContext();

  const getProductList = async (searchParams: GetProductSearchParams): Promise<ProductList | null> => {
    Logger.debug(`useProduct/${id}/getProductList`, searchParams);
    let products: ProductList = null;

    try {
      loading.value = true;
      products = await getProductListCommand.execute(context, searchParams);
      error.value.getProductList = null;
    } catch (err) {
      error.value.getProductList = err;
      Logger.error(`useProduct/${id}/search`, err);
    } finally {
      loading.value = false;
    }

    return products;
  };

  const getProductDetails = async (searchParams: GetProductSearchParams): Promise<ProductDetails | null> => {
    Logger.debug(`useProduct/${id}/getProductDetails`, searchParams);
    let products: ProductDetails = null;

    try {
      loading.value = true;
      products = await getProductDetailsCommand.execute(context, searchParams);
      error.value.getProductDetails = null;
    } catch (err) {
      error.value.getProductDetails = err;
      Logger.error(`useProduct/${id}/search`, err);
    } finally {
      loading.value = false;
    }

    return products;
  };

  const getProductPath = (product: ProductInterface) => {
    if (!product) return "/";
    return `/${product?.url_rewrites?.[0]?.url ?? product.url_key}`;
  };

  const getProductListBySkus = async (skus: string[]) => {
    try {
      loading.value = true;
      const { data, errors } = await context.app.$vsf.$magento.api.customQuery({
        query: GET_PRODUCT_SEARCH,
        queryVariables: { filter: { sku: { in: skus } } },
      });

      const typedData = data as unknown as ProductsResponse;

      error.value.getProductListBySkus = null;
      return typedData?.productSearch ?? { items: [] };
    } catch (err) {
      error.value.getProductListBySkus = err;
      Logger.error(`useProductsBySkus`, err);
    } finally {
      loading.value = false;
    }
  };

  const getProductListBySkusAndCateId = async (skus: string[], categoryId: string) => {
    try {
      loading.value = true;
      const { data, errors } = await context.app.$vsf.$magento.api.customQuery({
        query: getProductListBySkusCateIdGql,
        queryVariables: { filter: { category_id: { eq: categoryId }, sku: { in: skus } } },
      });

      const typedData = data as unknown as ProductsResponse;

      error.value.getProductListBySkusAndCateId = null;
      return typedData?.productSearch ?? { items: [] };
    } catch (err) {
      error.value.getProductListBySkusAndCateId = err;
      Logger.error(`useProductListBySkusAndCateId`, err);
    } finally {
      loading.value = false;
    }
  };

  const searchProducts = async (searchParams: GetProductSearchParams): Promise<ProductList | null> => {
    Logger.debug(`useProduct/${id}/searchProducts`, searchParams);
    let products: ProductList = null;

    try {

      loading.value = true;

      const { data } = await context.app.$vsf.$magento.api.customQuery({
        query: customSearchProductGql,
        queryVariables: searchParams,
      });
      const typedData = data as unknown as SearchProductsResponse;

      products = typedData?.productSearch ?? null;
      error.value.getProductList = null;
    } catch (err) {
      error.value.getProductList = err;
      Logger.error(`useProduct/${id}/search`, err);
    } finally {
      loading.value = false;
    }

    return products;
  };
  return {
    getProductList,
    getProductDetails,
    getProductPath,
    getProductListBySkus,
    getProductListBySkusAndCateId,
    searchProducts,
    error: readonly(error),
    loading: readonly(loading),
  };
};

export * from "./useProduct";
export default useProduct;
